import React, { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaLock } from 'react-icons/fa';
import { FaEnvelope } from 'react-icons/fa';
import { AUTH_BASE_URL } from '../../config/constants';
import userSessionService from '../../services/UserSessionService';
import { register, forgotPassword } from '../../services/AuthService';
import { usePublicConfig } from '../../hooks/usePublicConfig';
import '../../styles/Home.css';
import '../../styles/LoginModal.css';

const MODE_LOGIN = 'login';
const MODE_REGISTER = 'register';
const MODE_FORGOT = 'forgot';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.redirectAfterLogin] - Chemin de redirection après connexion réussie (ex. /dashboard)
 * @param {Function} [props.navigate] - Fonction de navigation React Router (pour redirection sans rechargement)
 */
const LoginModal = ({ isOpen, onClose, redirectAfterLogin = '', navigate }) => {
  const { config } = usePublicConfig();
  const [mode, setMode] = useState(MODE_LOGIN);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setError('');
      setSuccessMessage('');
      setFormData({ email: '', username: '', password: '' });
      setMode(MODE_LOGIN);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMessage('');
    setFormData((prev) => ({ ...prev, password: '' }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        try {
          const verifyPromise = userSessionService.verify();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 5000)
          );
          await Promise.race([verifyPromise, timeoutPromise]);
        } catch (verifyError) {
          console.error('Erreur vérification session:', verifyError);
        }
        setIsLoading(false);
        onClose();
        if (redirectAfterLogin && typeof navigate === 'function') {
          navigate(redirectAfterLogin);
        } else {
          window.location.reload();
        }
      } else {
        setError(data.error || 'Erreur de connexion');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Erreur de connexion:', err);
      setError('Erreur lors de la connexion. Veuillez réessayer.');
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Veuillez saisir votre adresse email');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    const result = await register({
      email: formData.email.trim(),
      username: formData.username?.trim() || undefined,
    });
    setIsLoading(false);
    if (result.success) {
      setSuccessMessage(
        "Vérifiez votre boîte mail. Vous recevrez un lien pour définir votre mot de passe."
      );
    } else {
      setError(result.error || 'Erreur lors de l\'inscription');
      if (result.details?.length) {
        setError(result.details.map((d) => d.msg).join(', '));
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Veuillez saisir votre adresse email');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    const result = await forgotPassword({ email: formData.email.trim() });
    setIsLoading(false);
    if (result.success) {
      setSuccessMessage(
        "Si un compte existe avec cette adresse, vous recevrez un email pour réinitialiser votre mot de passe."
      );
    } else {
      setError(result.error || 'Erreur lors de la demande');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      setError('');
      onClose();
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const getTitle = () => {
    if (mode === MODE_LOGIN) return 'Se connecter';
    if (mode === MODE_REGISTER) return 'Créer un compte';
    return 'Mot de passe oublié';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="login-modal">
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isLoading}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          {mode === MODE_LOGIN && (
            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label htmlFor="email">Email ou nom d'utilisateur</label>
                <div className="input-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="votre@email.com"
                    disabled={isLoading}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="password">Mot de passe</label>
                <div className="input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Votre mot de passe"
                    disabled={isLoading}
                    required
                    autoComplete="current-password"
                  />
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              <button
                type="submit"
                className="login-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </button>
              <div className="modal-footer auth-footer">
                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={() => switchMode(MODE_FORGOT)}
                >
                  Mot de passe oublié ?
                </button>
                {config.accountCreationEnabled && (
                  <>
                    <span className="auth-link-sep">·</span>
                    <button
                      type="button"
                      className="auth-link-btn"
                      onClick={() => switchMode(MODE_REGISTER)}
                    >
                      Créer un compte
                    </button>
                  </>
                )}
              </div>
            </form>
          )}

          {mode === MODE_REGISTER && (
            <form onSubmit={handleRegister}>
              <div className="form-group">
                <label htmlFor="reg-email">Email</label>
                <div className="input-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    id="reg-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="votre@email.com"
                    disabled={isLoading}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="reg-username">
                  Nom d'utilisateur (optionnel)
                </label>
                <div className="input-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    id="reg-username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="mon_pseudo"
                    disabled={isLoading}
                    autoComplete="username"
                  />
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              {successMessage && (
                <div className="success-message">{successMessage}</div>
              )}
              <button
                type="submit"
                className="login-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Inscription...' : "S'inscrire"}
              </button>
              <div className="modal-footer auth-footer">
                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={() => switchMode(MODE_LOGIN)}
                >
                  Déjà inscrit ? Se connecter
                </button>
              </div>
            </form>
          )}

          {mode === MODE_FORGOT && (
            <form onSubmit={handleForgotPassword}>
              <div className="form-group">
                <label htmlFor="forgot-email">Email</label>
                <div className="input-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    id="forgot-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="votre@email.com"
                    disabled={isLoading}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              {successMessage && (
                <div className="success-message">{successMessage}</div>
              )}
              <button
                type="submit"
                className="login-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Envoi...' : 'Envoyer le lien'}
              </button>
              <div className="modal-footer auth-footer">
                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={() => switchMode(MODE_LOGIN)}
                >
                  Retour à la connexion
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
